import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { CartStore } from '@/types/cart';

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: item => {
        const existingItem = get().items.find(i => i.id === item.id);

        if (existingItem) {
          const newQuantity = existingItem.quantity + (item.quantity || 1);
          // 기존 아이템의 구매 제한 확인 (기본값 50 설정)
          const purchaseLimit = existingItem.purchaseLimit || 50;
          const limitedQuantity = Math.min(newQuantity, purchaseLimit);

          set(state => ({
            items: state.items.map(i =>
              i.id === item.id
                ? {
                    ...i,
                    quantity: limitedQuantity,
                    purchaseLimit: purchaseLimit, // 기존 아이템에 purchaseLimit가 없으면 추가
                  }
                : i
            ),
          }));
        } else {
          // 새 아이템 추가 시 purchaseLimit 기본값 설정
          const newItem = {
            ...item,
            quantity: item.quantity || 1,
            purchaseLimit: item.purchaseLimit || 50, // 기본 제한값 50
          };

          set(state => ({
            items: [...state.items, newItem],
          }));
        }
      },

      removeItem: id => {
        set(state => ({
          items: state.items.filter(item => item.id !== id),
        }));
      },

      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }

        set(state => ({
          items: state.items.map(item => {
            if (item.id === id) {
              // 구매 제한 확인 후 제한된 수량으로 설정
              const limitedQuantity = Math.min(quantity, item.purchaseLimit);
              return { ...item, quantity: limitedQuantity };
            }
            return item;
          }),
        }));
      },

      updateItemPrice: (id, newPrice) => {
        set(state => ({
          items: state.items.map(item => (item.id === id ? { ...item, price: newPrice } : item)),
        }));
      },

      updateItemOption: (id, option) => {
        set(state => ({
          items: state.items.map(item => (item.id === id ? { ...item, option } : item)),
        }));
      },

      updateItemSelectedOptions: (id, selectedOptions) => {
        set(state => ({
          items: state.items.map(item => (item.id === id ? { ...item, selectedOptions } : item)),
        }));
      },

      clearCart: () => {
        set({ items: [] });
      },

      getTotalPrice: () => {
        return get().items.reduce((total, item) => total + item.price * item.quantity, 0);
      },

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
    }),
    {
      name: 'cart-storage', // localStorage 키 이름
      partialize: state => ({ items: state.items }), // items만 영속화
    }
  )
);
